import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  ProcCreationData,
  ProcProcessingEventType,
} from '@/entities/proc/proc-generation.types';
import { useSocketSubscription } from '@/shared/api';
import { usePersistentStorage, PersistentStorageKeys } from '@/shared/hooks';

/**
 * Base processing state interface
 */
interface BaseProcessingState {
  type: ProcessingType;
  progress: {
    value: number;
    message: string;
  };
  isError?: boolean;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Proc generation specific state
 */
interface ProcProcessingState extends BaseProcessingState {
  type: ProcessingType.PROC_PROCESSING;
  prompt: string;
}

/**
 * Union of all processing states
 */
export type ProcessingState = ProcProcessingState | BaseProcessingState;

/**
 * Available processing types in the application
 */
export enum ProcessingType {
  PROC_PROCESSING = 'proc_processing',
  FILE_PROCESSING = 'file_processing',
  KNOWLEDGE_BASE_INDEXING = 'knowledge_base_indexing',
  // Add more processing types as needed
}

/**
 * Query key factory for different processing states
 */
const getProcessingStateKey = (type: ProcessingType) =>
  ['processing', type, 'state'] as const;

/**
 * Socket configuration for different processing types
 */
const getSocketConfig = <T extends ProcessingState>({
  type,
  updateState,
  onSuccess,
  onError,
  onStart,
}: {
  type: ProcessingType;
  updateState: (id: string, updates: Partial<T>) => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onStart?: () => void;
}) => {
  switch (type) {
    case ProcessingType.PROC_PROCESSING: {
      const updateProcProcessingState = updateState as (
        id: string,
        updates: Partial<ProcProcessingState>,
      ) => void;

      return [
        {
          event: ProcProcessingEventType.START,
          onMessage: (data: ProcCreationData) => {
            if (data.isError) {
              updateProcProcessingState(data.procId, {
                type: ProcessingType.PROC_PROCESSING,
                isError: true,
                errorMessage: data.error || 'An unexpected error occurred',
              });
              onError?.(data.error || 'An unexpected error occurred');
              return;
            }

            updateProcProcessingState(data.procId, {
              type: ProcessingType.PROC_PROCESSING,
              progress: data.progress,
              prompt: data.message || '',
              isError: false,
            });
            onStart?.();
          },
        },
        {
          event: ProcProcessingEventType.GENERATING,
          onMessage: (data: ProcCreationData) => {
            if (data.isError) {
              updateProcProcessingState(data.procId, {
                type: ProcessingType.PROC_PROCESSING,
                isError: true,
                errorMessage: data.error || 'An unexpected error occurred',
              });
              onError?.(data.error || 'An unexpected error occurred');
              return;
            }

            updateProcProcessingState(data.procId, {
              type: ProcessingType.PROC_PROCESSING,
              progress: data.progress,
            });
          },
        },
        {
          event: ProcProcessingEventType.RUN_END,
          onMessage: (data: ProcCreationData) => {
            if (data.isError) {
              updateProcProcessingState(data.procId, {
                type: ProcessingType.PROC_PROCESSING,
                isError: true,
                errorMessage: data.error || 'An unexpected error occurred',
              });
              onError?.(data.error || 'An unexpected error occurred');
              return;
            }

            updateProcProcessingState(data.procId, {
              type: ProcessingType.PROC_PROCESSING,
              progress: data.progress,
              isError: false,
            });
            onSuccess?.();
          },
        },
      ];
    }
    // Add more cases for other processing types as needed
    default:
      return [];
  }
};

/**
 * Hook for managing any processing state that needs to be tracked globally.
 * This provides a centralized way to manage state for any entity that is:
 * - Being processed
 * - Being generated
 * - Being indexed
 * - Or any other long-running operation
 *
 * The state is stored in both React Query's cache and localStorage to persist
 * across page refreshes and tabs. It also manages socket subscriptions for
 * real-time updates.
 *
 * @param type - The type of processing being managed
 * @param options - Optional callbacks for success and error states
 */
export const useProcessingState = <T extends ProcessingState = ProcessingState>(
  type: ProcessingType,
  options: {
    onSuccess?: () => void;
    onError?: (error: string) => void;
    onStart?: () => void;
  } = {},
) => {
  const queryClient = useQueryClient();
  const queryKey = getProcessingStateKey(type);
  const [persistedState, setPersistedState] = usePersistentStorage<
    Record<string, T>
  >(PersistentStorageKeys.GeneratingProcs, {});

  const { data: processingStates = {} } = useQuery<Record<string, T>>(
    queryKey,
    () => {
      const current = queryClient.getQueryData<Record<string, T>>(queryKey);
      return current || persistedState;
    },
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      initialData: persistedState,
    },
  );

  const updateState = (id: string, updates: Partial<T>) => {
    queryClient.setQueryData(queryKey, (current: Record<string, T> = {}) => {
      const existingState = current[id];
      const newState = {
        ...current,
        [id]: {
          ...existingState,
          ...updates,
        },
      };

      // Persist to storage
      setPersistedState(newState);

      return newState;
    });
  };

  const removeState = (id: string) => {
    queryClient.setQueryData(queryKey, (current: Record<string, T> = {}) => {
      const newState = Object.entries(current)
        .filter(([currentId]) => currentId !== id)
        .reduce(
          (acc, [currentId, value]) => ({ ...acc, [currentId]: value }),
          {},
        );

      // Persist to storage
      setPersistedState(newState);

      return newState;
    });
  };

  const socketConfig = getSocketConfig({
    type,
    updateState,
    onSuccess: options.onSuccess,
    onError: options.onError,
    onStart: options.onStart,
  });

  useSocketSubscription(socketConfig);

  return {
    processingStates,
    updateState,
    removeState,
  };
};
